import { useState, useMemo, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Editor from '@monaco-editor/react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Separator } from '@/shared/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/dialog';
import { Loader2, ArrowLeft, Code2, Eye, Settings2, Trash2, Plus, RotateCcw } from 'lucide-react';
import { useToast } from '@/shared/hooks/use-toast';
import { useTheme } from '@/shared/hooks/use-theme';
import { EmailTemplateService } from '../services/emailTemplateService';
import type { EmailTemplate, EmailTemplateVariable } from '../types/EmailTemplateTypes';

function renderVariables(html: string, variables: Record<string, string>): string {
  return Object.entries(variables).reduce(
    (acc, [key, value]) => acc.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value),
    html
  );
}

interface VariablesDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  variables: EmailTemplateVariable[];
  onChange: (v: EmailTemplateVariable[]) => void;
}

function VariablesDialog({ open, onOpenChange, variables, onChange }: VariablesDialogProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<EmailTemplateVariable[]>(variables);

  useEffect(() => {
    if (open) setDraft(variables);
    // Intentional: we reset the local draft only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const hasEmptyKey = draft.some((v) => v.key.trim() === '');

  const updateField = (index: number, field: keyof EmailTemplateVariable, value: string) => {
    setDraft((prev) => prev.map((v, i) => (i === index ? { ...v, [field]: value } : v)));
  };

  const addVariable = () => {
    setDraft((prev) => [...prev, { key: '', description: '', example: '' }]);
  };

  const removeVariable = (index: number) => {
    setDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    onChange(draft);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('settings.emailTemplates.editor.manageVariables')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-2">
          {draft.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('settings.emailTemplates.editor.noVariables')}</p>
          ) : (
            <>
              <div className="grid grid-cols-[1fr_1.5fr_1.5fr_auto] gap-2 px-1">
                <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('settings.emailTemplates.editor.variableKey')}
                </span>
                <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('settings.emailTemplates.editor.variableDescription')}
                </span>
                <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('settings.emailTemplates.editor.variableExample')}
                </span>
                <span />
              </div>
              {draft.map((v, i) => (
                <div key={i} className="grid grid-cols-[1fr_1.5fr_1.5fr_auto] items-center gap-2">
                  <Input
                    value={v.key}
                    onChange={(e) => updateField(i, 'key', e.target.value)}
                    placeholder="key"
                    className="font-mono text-xs"
                  />
                  <Input
                    value={v.description}
                    onChange={(e) => updateField(i, 'description', e.target.value)}
                    placeholder="description"
                    className="text-xs"
                  />
                  <Input
                    value={v.example}
                    onChange={(e) => updateField(i, 'example', e.target.value)}
                    placeholder="example"
                    className="text-xs"
                  />
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => removeVariable(i)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </>
          )}

          <Button variant="outline" size="sm" className="mt-1 w-fit" onClick={addVariable}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            {t('settings.emailTemplates.editor.addVariable')}
          </Button>

          {hasEmptyKey && (
            <p className="text-xs text-destructive">{t('settings.emailTemplates.editor.keyRequired')}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleSave} disabled={hasEmptyKey}>
            {t('common.save', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface EmailTemplateEditorProps {
  template: EmailTemplate;
  slugTemplates: EmailTemplate[];
  onBack: () => void;
}

export const EmailTemplateEditor = ({ template, slugTemplates, onBack }: EmailTemplateEditorProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { theme } = useTheme();
  const queryClient = useQueryClient();

  const [activeLanguage, setActiveLanguage] = useState(template.language);
  const activeTemplate = slugTemplates.find(tpl => tpl.language === activeLanguage) ?? template;

  const [subject, setSubject] = useState(template.subject);
  const [content, setContent] = useState(template.content);
  const [activeTab, setActiveTab] = useState<'code' | 'preview'>('code');
  const [variables, setVariables] = useState<EmailTemplateVariable[]>(template.variables);
  const [variablesOpen, setVariablesOpen] = useState(false);

  const switchLanguage = (lang: string) => {
    const tpl = slugTemplates.find(tpl => tpl.language === lang);
    if (!tpl || lang === activeLanguage) return;
    setActiveLanguage(lang);
    setSubject(tpl.subject);
    setContent(tpl.content);
    setVariables(tpl.variables);
  };
  const editorRef = useRef<Parameters<NonNullable<React.ComponentProps<typeof Editor>['onMount']>>[0] | null>(null);

  const exampleVariables = useMemo(
    () => variables.reduce<Record<string, string>>(
      (acc, v) => ({ ...acc, [v.key]: v.example }),
      {}
    ),
    [variables]
  );

  const rendered = useMemo(
    () => renderVariables(content, exampleVariables),
    [content, exampleVariables]
  );

  const saveMutation = useMutation({
    mutationFn: () =>
      EmailTemplateService.updateOrgTemplate(activeTemplate.slug, activeTemplate.language, {
        subject, content, variables,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      toast({ title: t('settings.emailTemplates.saveSuccess') });
    },
    onError: () => {
      toast({ title: t('settings.emailTemplates.saveError'), variant: 'destructive' });
    },
  });

  const revertMutation = useMutation({
    mutationFn: () => EmailTemplateService.revertOrgTemplateToBase(activeTemplate.slug, activeTemplate.language),
    onSuccess: (reverted) => {
      setSubject(reverted.subject);
      setContent(reverted.content);
      setVariables(reverted.variables);
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      toast({ title: t('settings.emailTemplates.revertSuccess', 'Template reverted to original') });
    },
    onError: () => {
      toast({ title: t('settings.emailTemplates.revertError', 'Failed to revert template'), variant: 'destructive' });
    },
  });

  const insertVariable = (key: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    const selection = editor.getSelection();
    if (!selection) return;
    editor.executeEdits('insert-variable', [
      { range: selection, text: `{{${key}}}`, forceMoveMarkers: true },
    ]);
    editor.focus();
  };

  return (
    <div className="flex flex-col gap-6">

      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" className="-ml-2 text-muted-foreground hover:text-foreground" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Separator orientation="vertical" className="h-5" />
        <div className="flex flex-1 items-center justify-between gap-4">
          <div className="flex items-baseline gap-2">
            <span className="font-semibold">{activeTemplate.name}</span>
            <span className="font-mono text-xs text-muted-foreground">{activeTemplate.slug}</span>
          </div>
          {slugTemplates.length > 1 && (
            <div className="flex items-center gap-0.5 rounded-md border bg-background p-0.5">
              {slugTemplates.map((tpl) => (
                <button
                  key={tpl.language}
                  onClick={() => switchLanguage(tpl.language)}
                  className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                    tpl.language === activeLanguage
                      ? 'bg-muted text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {tpl.language}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Subject */}
      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {t('settings.emailTemplates.editor.subject')}
        </span>
        <Input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="bg-muted/40 border-0 focus-visible:ring-1"
        />
      </div>

      {/* Editor */}
      <div className="flex flex-col gap-0 overflow-hidden rounded-lg border">
        {/* Editor toolbar */}
        <div className="flex items-center justify-between border-b bg-muted/30 px-3 py-1.5">
          <span className="text-xs font-medium text-muted-foreground">
            {activeTab === 'code' ? 'HTML' : 'Preview'}
          </span>
          <div className="flex items-center gap-0.5 rounded-md border bg-background p-0.5">
            <button
              onClick={() => setActiveTab('code')}
              className={`flex items-center gap-1.5 rounded px-2.5 py-1 text-xs transition-colors ${activeTab === 'code'
                  ? 'bg-muted text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
                }`}
            >
              <Code2 className="h-3 w-3" />
              {t('settings.emailTemplates.editor.content')}
            </button>
            <button
              onClick={() => setActiveTab('preview')}
              className={`flex items-center gap-1.5 rounded px-2.5 py-1 text-xs transition-colors ${activeTab === 'preview'
                  ? 'bg-muted text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
                }`}
            >
              <Eye className="h-3 w-3" />
              Preview
            </button>
          </div>
        </div>

        {/* Editor content */}
        <div style={{ height: '560px' }}>
          {activeTab === 'code' ? (
            <Editor
              height="560px"
              language="html"
              value={content}
              onChange={(val) => setContent(val ?? '')}
              theme={theme === 'dark' ? 'vs-dark' : 'vs'}
              options={{ minimap: { enabled: false }, wordWrap: 'on', fontSize: 13, lineHeight: 20 }}
              onMount={(editor) => { editorRef.current = editor; }}
            />
          ) : (
            <iframe
              srcDoc={rendered}
              title="template-preview"
              style={{ width: '100%', height: '100%', border: 'none' }}
              sandbox="allow-same-origin"
            />
          )}
        </div>
      </div>

      {/* Variables */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {t('settings.emailTemplates.editor.variables')}
        </span>
        <Separator orientation="vertical" className="h-3.5" />
        {variables.map((v) => (
          <button key={v.key} title={v.description} onClick={() => insertVariable(v.key)}
            className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground">
            {`{{${v.key}}}`}
          </button>
        ))}
        <Button variant="ghost" size="sm" className="h-5 px-1.5 text-xs text-muted-foreground"
          onClick={() => setVariablesOpen(true)}>
          <Settings2 className="h-3 w-3" />
        </Button>
      </div>

      <VariablesDialog
        open={variablesOpen}
        onOpenChange={setVariablesOpen}
        variables={variables}
        onChange={setVariables}
      />

      {/* Actions */}
      <div className="flex items-center justify-between border-t pt-4">
        <Button
          variant="outline"
          onClick={() => revertMutation.mutate()}
          disabled={revertMutation.isPending || saveMutation.isPending}
        >
          {revertMutation.isPending
            ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            : <RotateCcw className="mr-2 h-4 w-4" />}
          {t('settings.emailTemplates.editor.revertToOriginal', 'Revert to original')}
        </Button>
        <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || revertMutation.isPending}>
          {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {saveMutation.isPending
            ? t('settings.emailTemplates.editor.saving')
            : t('settings.emailTemplates.editor.save')}
        </Button>
      </div>

    </div>
  );
};
